import { Box, type SxProps, type Theme } from '@mui/material';
import { Link } from 'found';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
	borderRadius,
	boxShadows,
	cardDimensions,
	palette,
} from '../../../../assets/styles/themes/tokens';
import { getCardAriaLabel } from '../../../../utils/getCardAriaLabel';
import { getCardLinkProps, indexToPosition } from '../../../../utils/searchUtils';
import useRouter from '../../../../utils/useRouter';
import useScreenSize from '../../../../utils/useScreenSize';
import type { MuiCardBaseProps } from './MuiCardBase';
import PlayIcon from './PlayIcon';

const MuiCardBaseCover: React.FC<{
	contentType: MuiCardBaseProps['contentType'];
	cover: string;
	isWide?: boolean;
	withPlayIcon?: boolean;
	itemLink: string;
	children?: React.ReactNode;
	isTruncated?: boolean;
	title?: string;
	index?: number;
	openInNewTab?: boolean;
}> = ({
	contentType,
	cover,
	itemLink,
	isWide,
	withPlayIcon,
	children,
	isTruncated,
	title,
	index,
	openInNewTab,
}) => {
	const { isXsScreen } = useScreenSize();
	const { t } = useTranslation();
	const { match } = useRouter();

	const linkProps = getCardLinkProps(itemLink, match.location.pathname, indexToPosition(index), {
		currentSearch: match.location.search,
		openInNewTab,
	});

	const wrapperSx = isXsScreen
		? {
				width: isWide ? 176 : 94,
				minWidth: isWide ? 176 : 94,
				height: isTruncated ? '100%' : 130,
				position: 'relative',
			}
		: {
				borderRadius: borderRadius.sm,
				width: isWide ? 376 : 198,
				minWidth: isWide ? 376 : 198,
				height: '100%',
				position: 'relative',
				':hover': {
					boxShadow: boxShadows.elevation26,
				},
			};

	const ariaLabel = getCardAriaLabel(contentType, title, t);

	return (
		<Link
			to={linkProps}
			aria-label={ariaLabel}
			target={openInNewTab ? '_blank' : undefined}
			rel={openInNewTab ? 'noopener noreferrer' : undefined}
		>
			<Box sx={wrapperSx}>
				{withPlayIcon && <PlayIcon size={isXsScreen ? 'small' : 'large'} />}
				<CoverImage
					cover={cover}
					title={title}
					data-testid={isXsScreen ? 'mui-card-mobile__cover-image' : 'mui-card-desktop__cover-image'}
				/>
				{children}
			</Box>
		</Link>
	);
};

export default MuiCardBaseCover;

export const CoverImage: React.FC<{
	cover: MuiCardBaseProps['cover'];
	className?: string;
	dataTestId?: string;
	customSx?: SxProps<Theme>;
	title?: string;
}> = ({ cover, className, dataTestId, customSx, title }) => {
	const { isXsScreen } = useScreenSize();
	const { t } = useTranslation();
	const altText = title ? t('Cover image for {{title}}', { title }) : t('Cover image');
	return (
		<Box
			component="img"
			src={cover}
			alt={altText}
			className={className}
			data-testid={dataTestId || 'cover-image'}
			sx={{
				height: isXsScreen ? cardDimensions.short.height : cardDimensions.long.height,
				width: '100%',
				borderRadius: borderRadius.sm,
				objectFit: 'cover',
				border: `${isXsScreen ? '0.476px' : '1px'} solid ${palette.elevation.outlined}`,
				':hover': {},
				...customSx,
			}}
		/>
	);
};
